import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plug, Filter, ChevronDown } from 'lucide-react';
import { useIntegrations } from '../hooks/useIntegrations';
import { Integration } from '../services/integrationsService';

// Helper function to get status color classes
const getStatusColorClasses = (status: string) => {
  switch (status.toLowerCase()) {
    case 'connected':
    case 'active':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'disconnected':
    case 'inactive':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'pending':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'available':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'coming_soon':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

// Helper function to get auth type color classes
const getAuthTypeColorClasses = (authType: string) => {
  switch (authType.toLowerCase()) {
    case 'oauth':
    case 'oauth2':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'api_key':
    case 'api-key':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'basic':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'token':
      return 'bg-teal-100 text-teal-700 border-teal-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

// Helper function to translate categories
const translateCategory = (category: string, t: any) => {
  return t(`integrations.categories.${category}`, category.replace(/_/g, ' '));
};

// Helper function to translate statuses
const translateStatus = (status: string, t: any) => {
  return t(`integrations.statuses.${status}`, status.replace(/_/g, ' '));
};

// Helper function to translate auth types
const translateAuthType = (authType: string, t: any) => {
  return t(`integrations.authTypes.${authType}`, authType.replace(/_/g, ' '));
};

// Helper function to get status emoji
const getStatusEmoji = (status: string) => {
  switch (status.toLowerCase()) {
    case 'connected':
    case 'active':
      return '✅';
    case 'disconnected':
    case 'inactive':
      return '❌';
    case 'pending':
      return '⏳';
    case 'available':
      return '🔵';
    case 'coming_soon':
      return '🚀';
    case 'maintenance':
      return '🔧';
    default:
      return '⚪';
  }
};

// Helper function to get category emoji
const getCategoryEmoji = (category: string) => {
  switch (category.toLowerCase()) {
    case 'link_shortener':
      return '🔗';
    case 'social_media':
      return '📱';
    case 'analytics':
      return '📊';
    case 'email_marketing':
      return '📧';
    case 'crm':
      return '👥';
    case 'payment':
      return '💳';
    case 'productivity':
      return '⚡';
    case 'design':
      return '🎨';
    case 'communication':
      return '💬';
    case 'automation':
      return '🤖';
    default:
      return '📁';
  }
};

// Status dropdown component
interface StatusDropdownProps {
  value: string;
  onChange: (value: string) => void;
  statuses: string[];
  className?: string;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  onChange,
  statuses,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    { id: 'all', label: t('integrations.allStatuses', 'All Statuses'), emoji: '🌐', description: t('integrations.statusDescriptions.all', 'View integrations in all statuses') },
    ...statuses.map(status => ({
      id: status,
      label: translateStatus(status, t),
      emoji: getStatusEmoji(status),
      description: t(`integrations.statusDescriptions.${status}`, `Filter by ${status} integrations`)
    }))
  ];

  const selectedOption = options.find(option => option.id === value) || options[0];

  return (
    <div className="relative min-w-[200px]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 ${className}`}
      >
        <span className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span>{selectedOption.emoji}</span>
          <span className="text-gray-700">{selectedOption.label}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 w-72 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-y-auto max-h-96">
            <div className="py-1">
              {options.map(option => (
                <button
                  key={option.id}
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex flex-col items-start gap-0.5 px-4 py-2.5 text-sm text-left ${
                    option.id === value
                      ? 'bg-primary-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{option.emoji}</span>
                    <span className={`${option.id === value ? 'text-primary-600 font-semibold' : 'text-gray-800 font-medium'}`}>{option.label}</span>
                  </div>
                  <p className={`text-xs ${option.id === value ? 'text-primary-500' : 'text-gray-500'} pl-6`}>{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Category dropdown component
interface CategoryDropdownProps {
  value: string;
  onChange: (value: string) => void;
  categories: string[];
  className?: string;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  value,
  onChange,
  categories,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    { id: 'all', label: t('integrations.allCategories', 'All Categories'), emoji: '📁', description: t('integrations.categoryDescriptions.all', 'View all integration categories') },
    ...categories.map(category => ({
      id: category,
      label: translateCategory(category, t),
      emoji: getCategoryEmoji(category),
      description: t(`integrations.categoryDescriptions.${category}`, `Filter by ${category} integrations`)
    }))
  ];

  const selectedOption = options.find(option => option.id === value) || options[0];

  return (
    <div className="relative min-w-[200px]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 ${className}`}
      >
        <span className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span>{selectedOption.emoji}</span>
          <span className="text-gray-700">{selectedOption.label}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 w-72 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-y-auto max-h-96">
            <div className="py-1">
              {options.map(option => (
                <button
                  key={option.id}
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex flex-col items-start gap-0.5 px-4 py-2.5 text-sm text-left ${
                    option.id === value
                      ? 'bg-primary-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{option.emoji}</span>
                    <span className={`${option.id === value ? 'text-primary-600 font-semibold' : 'text-gray-800 font-medium'}`}>{option.label}</span>
                  </div>
                  <p className={`text-xs ${option.id === value ? 'text-primary-500' : 'text-gray-500'} pl-6`}>{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Integration card component
const IntegrationCard: React.FC<{ integration: Integration }> = ({ integration }) => {
  const { t } = useTranslation();
  
  // Safely convert category to array format
  const categories = React.useMemo(() => {
    if (!integration.category) return [];
    if (Array.isArray(integration.category)) return integration.category;
    if (typeof integration.category === 'string') return [integration.category];
    return [];
  }, [integration.category]);
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 relative">
      {/* Status badge in top right */}
      <div className="absolute top-4 right-4">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColorClasses(integration.status)}`}
        >
          {translateStatus(integration.status, t)}
        </span>
      </div>

      {/* Header with logo and name */}
      <div className="flex items-center gap-4 mb-4 pr-20">
        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
          {integration.logoUrl ? (
            <img 
              src={integration.logoUrl} 
              alt={integration.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-6 h-6 text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg></div>';
                }
              }}
            />
          ) : (
            <Plug className="w-6 h-6 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{integration.name}</h3>
          {integration.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{integration.description}</p>
          )}
        </div>
      </div>

      {/* Category tags */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((cat, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
            >
              {translateCategory(cat, t)}
            </span>
          ))}
        </div>
      )}

      {/* Auth type only at bottom */}
      <div className="flex items-center justify-start">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getAuthTypeColorClasses(integration.authType)}`}
        >
          {translateAuthType(integration.authType, t)}
        </span>
      </div>
    </div>
  );
};

const Integrations: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const { data: integrationsData, isLoading, error } = useIntegrations();
  const integrations = integrationsData?.data?.integrations || [];

  useEffect(() => {
    document.title = t('integrations.title', 'Integrations');
  }, [t]);

  // Get unique categories and statuses for filters
  const categories = React.useMemo(() => {
    const allCategories = integrations.flatMap(integration => {
      if (!integration.category) return [];
      if (Array.isArray(integration.category)) return integration.category;
      if (typeof integration.category === 'string') return [integration.category];
      return [];
    });
    return Array.from(new Set(allCategories));
  }, [integrations]);

  const statuses = React.useMemo(() => {
    const allStatuses = integrations.map(integration => integration.status);
    return Array.from(new Set(allStatuses));
  }, [integrations]);

  // Filter integrations
  const filteredIntegrations = React.useMemo(() => {
    return integrations.filter(integration => {
      const matchesSearch = !searchQuery || 
        integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        integration.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || integration.status === statusFilter;
      
      // Handle category filtering for different data types
      const matchesCategory = categoryFilter === 'all' || (() => {
        if (!integration.category) return false;
        if (Array.isArray(integration.category)) {
          return integration.category.includes(categoryFilter);
        }
        if (typeof integration.category === 'string') {
          return integration.category === categoryFilter;
        }
        return false;
      })();
      
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [integrations, searchQuery, statusFilter, categoryFilter]);

  if (isLoading) {
    // Skeleton loader
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          </div>
          
          {/* Filters skeleton */}
          <div className="mb-6 flex flex-col md:flex-row gap-4">
            <div className="h-10 bg-gray-200 rounded-lg flex-1 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
          </div>
          
          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8 flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-600 mb-4">Failed to load integrations</p>
            <p className="text-gray-500">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      <div className="w-[95%] mx-auto py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-title mb-2">
            {t('integrations.title', 'Integrations')}
          </h1>
          <p className="mt-1 text-gray-500 font-body">
            {t('integrations.subtitle', 'Connect your favorite tools and services')}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('integrations.searchPlaceholder', 'Search integrations...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status filter */}
          <StatusDropdown
            value={statusFilter}
            onChange={setStatusFilter}
            statuses={statuses}
          />

          {/* Category filter */}
          <CategoryDropdown
            value={categoryFilter}
            onChange={setCategoryFilter}
            categories={categories}
          />
        </div>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {t('integrations.resultsCount', {
              count: filteredIntegrations.length,
              total: integrations.length,
              defaultValue: 'Showing {{count}} of {{total}} integrations'
            })}
          </p>
        </div>

        {/* Integrations grid */}
        {filteredIntegrations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard key={integration.id} integration={integration} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Plug className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('integrations.noResults', 'No integrations found')}
            </h3>
            <p className="text-gray-500">
              {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                ? t('integrations.noResultsFiltered', 'Try adjusting your search or filter criteria')
                : t('integrations.noIntegrations', 'No integrations are available at the moment')
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Integrations; 